/* assets/src/viewer/models/louvers.js
 * Pergola bioclimatica a lamelle orientabili (port :1778-1941) MA con architettura F6:
 *  - struttura (pilastri/travi) come mesh PERSISTENTI riposizionate/scalate in-place
 *  - lamelle via InstancedMesh (count + instanceMatrix, DynamicDrawUsage): MAI N mesh
 * update(dims) NON dispone/ricrea nulla: aggiorna trasformazioni e matrici istanza.
 */
import * as THREE from 'three';
import { getStructuralSupportPositions } from './support.js';

const PL = 0.15;       // sezione pilastro
const BW = 0.165;      // larghezza trave
const BH = 0.22;       // altezza trave
const H = 2.4;         // altezza pilastri
const LOUVER_WIDTH = 0.195;
const LOUVER_SPACING = 0.165;
const LOUVER_CAPACITY = 96;   // capacità massima istanze (no realloc nel range d'uso)
const MAX_SUPPORTS = 4;

export class LouversModel {
  constructor(materials, colors) {
    this.type = 'louvers';
    this.materials = materials;
    this.group = new THREE.Group();

    // Materiali (condivisi per il cross-fade colore)
    this.matStruttura = materials.structureMaterial(colors.structColor);
    this.matTrim = materials.detailMaterial(0x94a3b8, { roughness: 0.45, metalness: 0.5 });
    this.matDark = materials.detailMaterial(0x334155, { roughness: 0.62, metalness: 0.45 });
    this.matLamella = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(colors.fabricColor),
      roughness: 0.45, metalness: 0.18, clearcoat: 0.16, envMapIntensity: 0.55,
    });
    this.matLamella.userData.asgRole = 'louver';

    this._dummy = new THREE.Object3D();
    this._tilt = 0;
    this._lastConfig = null;

    this._buildStructure();
    this._buildLouvers();

    // Gruppo telo/lamelle esposto per l'animazione copertura.
    this.fabricGroup = this._fabricGroup;
    this.fabricGroup.userData.animationSpeed = 0.018;
    this.fabricGroup.userData.updateLayout = (prog) => this.updateLayout(prog);
  }

  // ---------- costruzione (una volta) ----------
  _makePillar() {
    const g = new THREE.Group();
    const outer = new THREE.Mesh(new THREE.BoxGeometry(PL, H, PL), this.matStruttura);
    outer.position.y = H / 2; outer.castShadow = true; outer.receiveShadow = true; g.add(outer);
    const base = new THREE.Mesh(new THREE.BoxGeometry(PL + 0.04, 0.025, PL + 0.04), this.matStruttura);
    base.position.y = 0.0125; base.castShadow = true; base.receiveShadow = true; g.add(base);
    const head = new THREE.Mesh(new THREE.BoxGeometry(PL + 0.01, 0.03, PL + 0.01), this.matStruttura);
    head.position.y = H - 0.015; head.castShadow = true; head.receiveShadow = true; g.add(head);
    return g;
  }

  // Trave a lunghezza UNITARIA in X (scalata via group.scale.x). Tutte le parti corrono lungo X.
  _makeBeam() {
    const g = new THREE.Group();
    const shell = new THREE.Mesh(new THREE.BoxGeometry(1, BH, BW), this.matStruttura);
    shell.castShadow = true; shell.receiveShadow = true; g.add(shell);
    const topCap = new THREE.Mesh(new THREE.BoxGeometry(1, 0.018, BW * 0.82), this.matTrim);
    topCap.position.set(0, BH * 0.34, -0.005); topCap.castShadow = true; g.add(topCap);
    const lowerRib = new THREE.Mesh(new THREE.BoxGeometry(1, 0.026, BW * 0.48), this.matDark);
    lowerRib.position.set(0, -BH * 0.16, BW * 0.08); g.add(lowerRib);
    return g;
  }

  _buildStructure() {
    this.cornerPillars = [];
    for (let i = 0; i < 4; i++) { const p = this._makePillar(); this.group.add(p); this.cornerPillars.push(p); }

    this.supportPillars = [];
    for (let i = 0; i < MAX_SUPPORTS * 2; i++) { const p = this._makePillar(); p.visible = false; this.group.add(p); this.supportPillars.push(p); }

    this.frontBeam = this._makeBeam(); this.group.add(this.frontBeam);
    this.rearBeam = this._makeBeam(); this.group.add(this.rearBeam);
    this.sideBeam1 = this._makeBeam(); this.sideBeam1.rotation.y = Math.PI / 2; this.group.add(this.sideBeam1);
    this.sideBeam2 = this._makeBeam(); this.sideBeam2.rotation.y = Math.PI / 2; this.group.add(this.sideBeam2);

    this.supportBeams = [];
    for (let i = 0; i < MAX_SUPPORTS; i++) { const b = this._makeBeam(); b.rotation.y = Math.PI / 2; b.visible = false; this.group.add(b); this.supportBeams.push(b); }
  }

  _buildLouvers() {
    this._fabricGroup = new THREE.Group();

    // Profilo lamella (port :1889-1901) a lunghezza UNITARIA lungo X.
    const lw = LOUVER_WIDTH; const lh = 0.028; const lip = 0.010;
    const shape = new THREE.Shape();
    shape.moveTo(-lw / 2, 0.004);
    shape.bezierCurveTo(-lw * 0.22, -lh, lw * 0.22, -lh, lw / 2, 0.004);
    shape.lineTo(lw / 2 - lip, lh * 0.78);
    shape.bezierCurveTo(lw * 0.20, lh * 0.38, -lw * 0.20, lh * 0.38, -lw / 2 + lip, lh * 0.78);
    shape.closePath();
    const bladeGeo = new THREE.ExtrudeGeometry(shape, { depth: 1, bevelEnabled: true, bevelSegments: 2, steps: 1, bevelSize: 0.0015, bevelThickness: 0.0015 });
    bladeGeo.translate(0, 0, -0.5);
    bladeGeo.rotateY(Math.PI / 2); // lunghezza -> X

    this.blades = new THREE.InstancedMesh(bladeGeo, this.matLamella, LOUVER_CAPACITY);
    this.blades.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.blades.castShadow = true; this.blades.receiveShadow = true;
    this.blades.count = 0;
    this._fabricGroup.add(this.blades);

    // Perni sulle rotaie (2 per lamella), non orientabili.
    const hingeGeo = new THREE.CylinderGeometry(0.01, 0.01, 0.05, 12);
    hingeGeo.rotateX(Math.PI / 2); // asse -> Z
    this.hinges = new THREE.InstancedMesh(hingeGeo, this.matTrim, LOUVER_CAPACITY * 2);
    this.hinges.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.hinges.count = 0;
    this._fabricGroup.add(this.hinges);

    this.group.add(this._fabricGroup);
  }

  // ---------- layout in-place ----------
  _layoutStructure(w, d, config) {
    const yBeam = H + BH / 2;
    const cp = this.cornerPillars;
    cp[0].position.set(-w / 2 + PL / 2, 0, d / 2 - PL / 2);
    cp[1].position.set(w / 2 - PL / 2, 0, d / 2 - PL / 2);
    cp[2].position.set(-w / 2 + PL / 2, 0, -d / 2 + PL / 2);
    cp[3].position.set(w / 2 - PL / 2, 0, -d / 2 + PL / 2);

    this.frontBeam.scale.x = w;
    this.frontBeam.position.set(0, yBeam, d / 2 - PL / 2 + 0.003);
    this.rearBeam.scale.x = w;
    this.rearBeam.position.set(0, yBeam, -d / 2 + PL / 2 - 0.003);

    const sideLen = d - PL * 2 + BW * 2;
    this.sideBeam1.scale.x = sideLen;
    this.sideBeam1.position.set(-w / 2 + PL / 2 - 0.003, yBeam, 0);
    this.sideBeam2.scale.x = sideLen;
    this.sideBeam2.position.set(w / 2 - PL / 2 + 0.003, yBeam, 0);

    // Supporti intermedi (pool show/hide)
    const positions = getStructuralSupportPositions(config || {}, w).slice(0, MAX_SUPPORTS);
    for (let i = 0; i < MAX_SUPPORTS; i++) {
      const front = this.supportPillars[i * 2];
      const back = this.supportPillars[i * 2 + 1];
      const beam = this.supportBeams[i];
      if (i < positions.length) {
        const x = positions[i];
        front.visible = true; front.position.set(x, 0, d / 2 - PL / 2);
        back.visible = true; back.position.set(x, 0, -d / 2 + PL / 2);
        beam.visible = true; beam.scale.x = sideLen; beam.position.set(x, yBeam, 0);
      } else {
        front.visible = false; back.visible = false; beam.visible = false;
      }
    }
  }

  _layoutLouvers(w, d) {
    this._fabricGroup.position.set(0, H + BH * 0.30, 0);
    const dInner = Math.max(0.4, d - PL * 2 + 0.02);
    const startZ = -dInner / 2 + LOUVER_WIDTH / 2 + 0.03;
    const endZ = dInner / 2 - LOUVER_WIDTH / 2 - 0.03;
    const realDist = Math.max(0.1, endZ - startZ);
    let num = Math.max(2, Math.floor(realDist / LOUVER_SPACING)) + 1;
    if (num > LOUVER_CAPACITY) num = LOUVER_CAPACITY;
    const spacing = num > 1 ? realDist / (num - 1) : realDist;
    const bladeLen = Math.max(0.6, w - PL - 0.03);

    this._louverState = { num, startZ, spacing, bladeLen, w };
    this._writeInstances();
  }

  _writeInstances() {
    const s = this._louverState;
    if (!s) return;
    const dummy = this._dummy;
    const railX = s.bladeLen / 2 - 0.02;

    for (let i = 0; i < s.num; i++) {
      const z = s.startZ + i * s.spacing;
      // lamella: scala in lunghezza (X), tilt attorno a X, posizione in Z
      dummy.position.set(0, 0, z);
      dummy.rotation.set(this._tilt, 0, 0);
      dummy.scale.set(s.bladeLen, 1, 1);
      dummy.updateMatrix();
      this.blades.setMatrixAt(i, dummy.matrix);

      // perni sx/dx (no tilt)
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(1, 1, 1);
      dummy.position.set(-railX, 0, z); dummy.updateMatrix(); this.hinges.setMatrixAt(i * 2, dummy.matrix);
      dummy.position.set(railX, 0, z); dummy.updateMatrix(); this.hinges.setMatrixAt(i * 2 + 1, dummy.matrix);
    }
    this.blades.count = s.num;
    this.hinges.count = s.num * 2;
    this.blades.instanceMatrix.needsUpdate = true;
    this.hinges.instanceMatrix.needsUpdate = true;
    this.blades.computeBoundingSphere();
  }

  // ---------- API controller ----------
  build(dims, config) { this.update(dims, config); }

  update(dims, config = null) {
    const cfg = config || this._lastConfig || {};
    this._lastConfig = cfg;
    this._layoutStructure(dims.w, dims.d, cfg);
    this._layoutLouvers(dims.w, dims.d);
  }

  setStructureColor(color) { this.materials.crossfadeColor(this.matStruttura, color); }
  setFabricColor(color) { this.materials.crossfadeColor(this.matLamella, color); }

  // animazione copertura: tilt lamelle
  updateLayout(prog) {
    this._tilt = THREE.MathUtils.degToRad(112) * THREE.MathUtils.clamp(prog, 0, 1);
    this._writeInstances();
  }

  dispose() {
    if (this.blades) this.blades.geometry.dispose();
    if (this.hinges) this.hinges.geometry.dispose();
    [this.matStruttura, this.matTrim, this.matDark, this.matLamella].forEach((m) => m && m.dispose());
  }
}
